import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { withoutPair } from "../components/team/DuplicateEmployees/duplicatePairs";
import { useAuth } from "../contexts/AuthProvider";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import type {
	DismissSuspectedDuplicateRequest,
	MergeCandidatePage,
	NurseMergePreview,
	NurseMergeRequest,
	NurseMergeResult,
	SuspectedDuplicatePage,
} from "../utils/nurseMergeModels";
import { normalizeNurseMergePreview } from "../utils/nurseMergeModels";
import { NURSES_QUERY_KEY_ROOT } from "./nursesQueryKeys";
import { NURSE_PROFILE_QUERY_KEY } from "./useInvalidateNurseQueries";

/** One page is the whole list the dialog shows; the search narrows it rather than paging through it. */
export const MERGE_CANDIDATE_PAGE_SIZE = 20;

export const NURSE_MERGE_CANDIDATES_QUERY_KEY = "nurseMergeCandidates";
export const NURSE_MERGE_PREVIEW_QUERY_KEY = "nurseMergePreview";
export const NURSE_MERGE_SUSPECTED_QUERY_KEY = "nurseMergeSuspected";

/** Where a merge was started from — reported on every merge event so the entry points can be told apart. */
export type MergeEntryPoint = "profile" | "team_duplicates_tab" | "onboarding_roster_upload";

/**
 * The suggestions are rebuilt from the whole network on every request (seconds, not milliseconds), and they
 * only change when records do — which the merge and dismiss mutations invalidate themselves.
 */
const SUSPECTED_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Records in the department's network that match `query` by name or exact id, excluding the one on screen.
 * SUPPORT-ONLY — disabled for everyone else so the support-gated endpoint is never called by an admin.
 * Pass an already-debounced query; an empty one fetches nothing.
 */
export const useMergeCandidates = ({ departmentId, query, excludeNurseId }: { departmentId: string; query: string; excludeNurseId: number }) => {
	const { userInfo } = useAuth();
	const trimmed = query.trim();
	return useQuery<MergeCandidatePage>({
		queryKey: [NURSE_MERGE_CANDIDATES_QUERY_KEY, departmentId, excludeNurseId, trimmed],
		queryFn: async () => (await backendService.searchMergeCandidates({ departmentId, query: trimmed, excludeNurseId, limit: MERGE_CANDIDATE_PAGE_SIZE })).data,
		enabled: userInfo.support && trimmed.length > 0 && excludeNurseId > 0,
		refetchOnWindowFocus: false,
	});
};

/**
 * What merging `duplicateNurseId` into `primaryNurseId` would do. Never cached across opens: the merge is
 * refused when the data moved since the preview, so a stale preview only sets the person up to fail.
 */
export const useNurseMergePreview = ({ primaryNurseId, duplicateNurseId }: { primaryNurseId: number | null; duplicateNurseId: number | null }) => {
	const { userInfo } = useAuth();
	return useQuery<NurseMergePreview>({
		queryKey: [NURSE_MERGE_PREVIEW_QUERY_KEY, primaryNurseId, duplicateNurseId],
		queryFn: async () => {
			if (primaryNurseId === null || duplicateNurseId === null) throw new Error("merge preview requested without both records");
			return normalizeNurseMergePreview((await backendService.getNurseMergePreview(primaryNurseId, duplicateNurseId)).data);
		},
		enabled: userInfo.support && primaryNurseId !== null && duplicateNurseId !== null,
		staleTime: 0,
		gcTime: 0,
		// Swapping which record is kept re-fetches with the ids reversed; the old preview stays on screen
		// (dimmed by the caller) instead of the comparison collapsing into a spinner for a moment.
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});
};

/**
 * Runs the merge. On success everything that lists or shows staff is refreshed, since one record is gone and
 * the other now carries its shifts. On failure the backend's own message is shown (it names what to do next)
 * and the preview is re-fetched, because the most common refusal — "the records changed" — is fixed by
 * looking again.
 */
export const useMergeNurses = ({
	departmentId,
	entryPoint,
	onMerged,
}: {
	departmentId: string;
	entryPoint: MergeEntryPoint;
	onMerged: (result: NurseMergeResult) => void;
}) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { showErrorSnackbar, refreshNursesData } = useHospitalData();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (request: NurseMergeRequest) => {
			track("nurse_merge_start", mergeAnalytics(departmentId, entryPoint, request));
			return (await backendService.mergeNurses(request)).data;
		},
		onSuccess: async (result, request) => {
			track("nurse_merge_success", {
				...mergeAnalytics(departmentId, entryPoint, request),
				merge_log_id: result.mergeLogId,
				retired_login: result.retiredUserId !== null,
			});
			showSnackbar(t("nurse.merge.success"), "success");
			// Stale, not refetched: the profile on screen may be the record that no longer exists, and refetching it
			// would 403. `onMerged` moves off it (or refetches the kept one); every other profile reloads when opened.
			queryClient.invalidateQueries({ queryKey: NURSE_PROFILE_QUERY_KEY, refetchType: "none" });
			queryClient.invalidateQueries({ queryKey: [NURSES_QUERY_KEY_ROOT] });
			queryClient.removeQueries({ queryKey: [NURSE_MERGE_CANDIDATES_QUERY_KEY] });
			queryClient.removeQueries({ queryKey: [NURSE_MERGE_PREVIEW_QUERY_KEY] });
			// One record is gone, so every suggested pair it was part of is gone or now points at the kept record.
			queryClient.invalidateQueries({ queryKey: [NURSE_MERGE_SUSPECTED_QUERY_KEY] });
			onMerged(result);
			// The merge already committed. A failed roster refresh must not route into onError and claim otherwise,
			// so it is reported on its own and the person is told to reload.
			refreshNursesData(departmentId).catch((refreshError: unknown) => {
				trackError("nurse_merge_refresh_error", {
					...mergeAnalytics(departmentId, entryPoint, request),
					error_message: refreshError instanceof Error ? refreshError.message : String(refreshError),
				});
				showSnackbar(t("nurse.merge.refresh.failed"), "warning");
			});
		},
		onError: (error, request) => {
			trackError("nurse_merge_error", {
				...mergeAnalytics(departmentId, entryPoint, request),
				error_message: error instanceof Error ? error.message : String(error),
			});
			showErrorSnackbar(t("nurse.merge.failed"), { action: "nurse_merge", department_id: departmentId }, error);
			queryClient.invalidateQueries({ queryKey: [NURSE_MERGE_PREVIEW_QUERY_KEY] });
		},
	});
};

const mergeAnalytics = (departmentId: string, entryPoint: MergeEntryPoint, request: NurseMergeRequest) => ({
	department_id: departmentId,
	entry_point: entryPoint,
	primary_nurse_id: request.primaryNurseId,
	duplicate_nurse_id: request.duplicateNurseId,
	department_choice_count: Object.keys(request.decisions.departmentChoices).length,
	has_shift_overlaps: request.decisions.overlapKeep !== null,
});

/**
 * One page of the pairs of records in the department's network that look like the same person, strongest
 * evidence first. SUPPORT-ONLY. The previous page stays on screen while the next one loads, since a load takes
 * seconds and an emptied table in between reads as "there are none".
 */
export const useSuspectedDuplicates = ({ departmentId, search, page, pageSize }: { departmentId: string; search: string; page: number; pageSize: number }) => {
	const { userInfo } = useAuth();
	// A blank search means every pair; the backend refuses a whitespace-only one.
	const query = search.trim() === "" ? null : search.trim();
	return useQuery<SuspectedDuplicatePage>({
		queryKey: [NURSE_MERGE_SUSPECTED_QUERY_KEY, departmentId, query, page, pageSize],
		queryFn: async () => (await backendService.getSuspectedDuplicates({ departmentId, query, limit: pageSize, offset: page * pageSize })).data,
		enabled: userInfo.support,
		staleTime: SUSPECTED_STALE_TIME_MS,
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});
};

/**
 * Marks two records as different people so the pair stops being suggested. The row leaves every cached page at
 * once; the pages are then refetched so the next pair moves up into the gap.
 */
export const useDismissSuspectedDuplicate = ({ departmentId, onDismissed }: { departmentId: string; onDismissed: () => void }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { showErrorSnackbar } = useHospitalData();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (request: DismissSuspectedDuplicateRequest) => {
			track("duplicate_employees_dismiss_start", dismissAnalytics(departmentId, request));
			return (await backendService.dismissSuspectedDuplicate(request)).data;
		},
		onSuccess: (_result, request) => {
			track("duplicate_employees_dismiss_success", dismissAnalytics(departmentId, request));
			queryClient.setQueriesData<SuspectedDuplicatePage>({ queryKey: [NURSE_MERGE_SUSPECTED_QUERY_KEY, departmentId] }, (page) =>
				page ? withoutPair(page, request.firstNurseId, request.secondNurseId) : page,
			);
			queryClient.invalidateQueries({ queryKey: [NURSE_MERGE_SUSPECTED_QUERY_KEY, departmentId] });
			showSnackbar(t("duplicates.dismiss.success"), "success");
			onDismissed();
		},
		onError: (error, request) => {
			trackError("duplicate_employees_dismiss_error", {
				...dismissAnalytics(departmentId, request),
				error_message: error instanceof Error ? error.message : String(error),
			});
			showErrorSnackbar(t("duplicates.dismiss.failed"), { action: "duplicate_employees_dismiss", department_id: departmentId }, error);
		},
	});
};

const dismissAnalytics = (departmentId: string, request: DismissSuspectedDuplicateRequest) => ({
	department_id: departmentId,
	first_nurse_id: request.firstNurseId,
	second_nurse_id: request.secondNurseId,
});
